(() => {
  if (customElements.get('dod-cart-page')) return;
  customElements.define('dod-cart-page', class extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      const signal = this.events.signal;
      this.copy = JSON.parse(this.querySelector('[data-page-copy]').textContent);
      this.sections = [this.dataset.section, 'cart-icon-bubble'];
      this.revision = 0;
      this.enable();
      this.addEventListener('click', event => {
        const remove = event.target.closest('[data-page-remove]');
        if (remove) {
          event.preventDefault();
          this.change(remove.closest('[data-line-key]'), 0, 'remove');
        }
        const step = event.target.closest('[data-step]');
        if (!step) return;
        const row = step.closest('[data-line-key]');
        const input = row.querySelector('[data-page-quantity]');
        const minimum = Number(input.dataset.min) || 1;
        let quantity = Number(input.value) + Number(step.dataset.step) * (Number(input.step) || 1);
        if (quantity < minimum) quantity = Number(step.dataset.step) < 0 ? 0 : minimum;
        this.change(row, quantity, step.dataset.step);
      }, {signal});
      this.addEventListener('change', event => {
        if (event.target.matches('[data-page-quantity]')) this.change(event.target.closest('[data-line-key]'), Number(event.target.value), 'input');
      }, {signal});
      this.addEventListener('keydown', event => {
        if (event.key === 'Enter' && event.target.matches('[data-page-quantity]')) {
          event.preventDefault();
          event.target.blur();
        }
      }, {signal});
      this.addEventListener('submit', event => {
        if (this.busy) event.preventDefault();
      }, {signal});
    }
    disconnectedCallback() { this.events?.abort(); this.events = null; }
    enable() {
      this.querySelectorAll('[data-page-enable]').forEach(button => { button.disabled = false; });
      this.querySelectorAll('[data-line-key]').forEach(row => {
        const input = row.querySelector('[data-page-quantity]');
        const plus = row.querySelector('[data-step="1"]');
        if (input && plus && input.max) plus.disabled = Number(input.value) >= Number(input.max);
      });
    }
    status(message) { this.querySelector('#cart-errors').textContent = message || ''; }
    async request(path, data) {
      const response = await fetch(this.dataset.root + path, data ? {
        method:'POST', headers:{'Content-Type':'application/json', Accept:'application/json'}, body:JSON.stringify(data)
      } : {headers:{Accept:'application/json'}});
      const result = await response.json();
      if (!response.ok || result.status || result.errors) throw new Error(typeof result.description === 'string' ? result.description : this.copy.error);
      return result;
    }
    async getSections(cart) {
      if (this.sections.every(key => cart?.sections?.[key])) return cart.sections;
      const url = new URL(location.href);
      url.searchParams.set('sections', this.sections.join(','));
      const response = await fetch(url.toString(), {cache:'no-store'});
      if (!response.ok) throw new Error(this.copy.error);
      return response.json();
    }
    render(sections) {
      const parser = new DOMParser();
      const page = parser.parseFromString(sections[this.dataset.section] || '', 'text/html').querySelector('dod-cart-page');
      if (!page) throw new Error(this.copy.error);
      this.innerHTML = page.innerHTML;
      this.dataset.empty = page.dataset.empty;
      const icon = document.getElementById('cart-icon-bubble');
      const iconHTML = sections['cart-icon-bubble'];
      if (icon && iconHTML) icon.innerHTML = parser.parseFromString(iconHTML, 'text/html').querySelector('.shopify-section')?.innerHTML || iconHTML;
      this.enable();
    }
    async refresh(cart) {
      if (this.busy) return;
      const revision = ++this.revision;
      try {
        const sections = await this.getSections(cart);
        if (revision === this.revision && !this.busy) { this.render(sections); this.status(cart?.gift_notice); }
      } catch (error) { if (!this.busy) this.status(error.message); }
    }
    async change(row, quantity, focus) {
      if (this.busy || window.DODGameTiers?.busy) { this.status(this.copy.busy); return; }
      const input = row.querySelector('[data-page-quantity]');
      const minimum = Number(input?.dataset.min) || 1;
      const increment = Number(input?.step) || 1;
      if (!Number.isInteger(quantity) || quantity < 0 || (quantity > 0 && (quantity < minimum || (quantity - minimum) % increment !== 0 || (input?.max && quantity > Number(input.max))))) {
        this.status(this.copy.quantity);
        await this.refresh();
        this.status(this.copy.quantity);
        return;
      }
      const variant = row.dataset.variant;
      await this.run(async () => this.request('cart/change.js', {id:row.dataset.lineKey, quantity, sections:this.sections, sections_url:location.pathname}), this.copy.updated, {variant, focus});
    }
    async addGame(button) {
      if (this.busy || window.DODGameTiers?.busy) { this.status(this.copy.busy); return; }
      const variant = button.dataset.upgradeAdd;
      const product = button.closest('[data-upgrade-game]').dataset.upgradeGame;
      await this.run(async () => {
        const cart = await this.request('cart.js');
        if (cart.items.some(item => String(item.product_id) === product)) throw new Error(this.copy.already);
        return this.request('cart/add.js', {items:[{id:Number(variant), quantity:1}], sections:this.sections, sections_url:location.pathname});
      }, this.copy.added, {variant, focus:'input'});
    }
    async run(action, message, target) {
      this.busy = true;
      this.revision++;
      const release = window.DODGameTiers?.begin();
      const controls = [...this.querySelectorAll('button,input')].filter(control => !control.disabled);
      controls.forEach(control => { control.disabled = true; });
      this.setAttribute('aria-busy', 'true');
      this.status(this.copy.updating);
      try {
        let cart = await action();
        if (window.DODGameTiers) cart = await window.DODGameTiers.sync(cart, this.sections);
        this.render(await this.getSections(cart));
        this.status(cart.gift_notice || message);
      } catch (error) {
        try { this.render(await this.getSections()); } catch (_) {}
        this.status(error.message || this.copy.error);
      } finally {
        this.busy = false;
        controls.forEach(control => { if (control.isConnected) control.disabled = false; });
        release?.();
        this.removeAttribute('aria-busy');
        this.enable();
      }
      const row = [...this.querySelectorAll('[data-line-key]')].find(item => item.dataset.variant === target.variant);
      const selector = target.focus === 'input' ? '[data-page-quantity]' : target.focus === 'remove' ? '[data-page-remove]' : `[data-step="${target.focus}"]`;
      (row?.querySelector(selector) || this.querySelector('h1')).focus({preventScroll:true});
    }
  });
})();
